import { CurrencyValue, DateValue, EmptyState, PageHeader, StatusBadge } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useAssetDisposals, useAssetSummary, useMaintenanceDue } from '../api'
import { DISPOSAL_METHOD_LABELS, MAINTENANCE_KIND_LABELS } from '../types'
import type { DisposalMethod, MaintenanceKind } from '../types'

/** The three questions asked of an asset register that the register itself does not answer. */
export function AssetReportsPage() {
  useBreadcrumb()
  const summary = useAssetSummary()
  const due = useMaintenanceDue()
  const disposals = useAssetDisposals()

  return (
    <div className="space-y-4">
      <PageHeader
        title="Asset reports"
        subtitle="What is owed attention, what has left, and what any of it is worth."
      />

      <Card className="space-y-2 p-4 text-sm">
        <h2 className="font-semibold text-[var(--color-heading)]">Valuation</h2>
        {summary.isPending && <Skeleton className="h-16" />}
        {summary.isSuccess && (
          <>
            <p>
              {summary.data.purchaseCost == null
                ? 'No purchase total is shown.'
                : (
                  <>
                    Bought for{' '}
                    <CurrencyValue
                      value={summary.data.purchaseCost}
                      currency={summary.data.currency ?? undefined}
                    />
                    {summary.data.bookValue != null && (
                      <>
                        , worth{' '}
                        <CurrencyValue
                          value={summary.data.bookValue}
                          currency={summary.data.currency ?? undefined}
                        />{' '}
                        today
                      </>
                    )}
                    .
                  </>
                )}
            </p>
            <p className="text-slate-600">{summary.data.valuationNote}</p>
          </>
        )}
      </Card>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">
          Maintenance that is due
        </h2>
        {due.isPending && <Skeleton className="m-4 h-16" />}
        {due.isSuccess && due.data.length === 0 && (
          <EmptyState
            title="Nothing is overdue"
            description="Maintenance that was scheduled and has not been recorded as done appears here."
          />
        )}
        {due.isSuccess && due.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Asset</th>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Was due</th>
                  <th className="p-3 text-start font-medium">Vendor</th>
                </tr>
              </thead>
              <tbody>
                {due.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="p-3 font-mono">{row.assetTag}</td>
                    <td className="p-3">
                      {MAINTENANCE_KIND_LABELS[row.kind as MaintenanceKind] ?? row.kind}
                    </td>
                    <td className="p-3">
                      <DateValue value={row.scheduledOn} />
                      {row.overdue && (
                        <span className="ms-2 text-xs font-medium text-amber-700">overdue</span>
                      )}
                    </td>
                    <td className="p-3">{row.vendor ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">Disposals</h2>
        {disposals.isPending && <Skeleton className="m-4 h-16" />}
        {disposals.isSuccess && disposals.data.length === 0 && (
          <EmptyState
            title="Nothing has been disposed of"
            description="A disposal records how an asset left, what it fetched, and what the books said it was worth."
          />
        )}
        {disposals.isSuccess && disposals.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Asset</th>
                  <th className="p-3 text-start font-medium">On</th>
                  <th className="p-3 text-start font-medium">How</th>
                  <th className="p-3 text-end font-medium">Proceeds</th>
                  <th className="p-3 text-end font-medium">Book value</th>
                  <th className="p-3 text-end font-medium">Gain / loss</th>
                </tr>
              </thead>
              <tbody>
                {disposals.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">
                      <span className="font-mono">{row.assetTag}</span>
                      <span className="ms-2 text-slate-600">{row.assetName}</span>
                    </td>
                    <td className="p-3"><DateValue value={row.disposedOn} /></td>
                    <td className="p-3">
                      <StatusBadge
                        status={row.method}
                        tone="neutral"
                        label={DISPOSAL_METHOD_LABELS[row.method as DisposalMethod] ?? row.method}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={row.proceeds} currency={row.currency ?? undefined} />
                    </td>
                    <td className="p-3 text-end">
                      {row.bookValue == null
                        ? <span className="text-xs text-slate-500" title={row.valuationNote ?? ''}>
                            Not computable
                          </span>
                        : <CurrencyValue value={row.bookValue} currency={row.currency ?? undefined} />}
                    </td>
                    <td className={`p-3 text-end ${
                      row.gainLoss != null && row.gainLoss < 0 ? 'text-rose-700' : ''
                    }`}>
                      {row.gainLoss == null
                        ? <span className="text-xs text-slate-500">—</span>
                        : <CurrencyValue value={row.gainLoss} currency={row.currency ?? undefined} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
